import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { listInHouse } from "../../services/inHouseService";
import { useProviderLogos } from "../../hooks/useProviderLogo";
import { ErrorNote, IntegrationLogo, StatusBadge, statusOf } from "./parts";

/**
 * The In-House Integrations tab for org admins: an "Add your API" tile plus a
 * card per in-house integration (draft, live or disabled). Rendered inside the
 * Dashboard grid; only admins can author, so only admins see this.
 * `onLoaded` gets the ids shown here so the Dashboard doesn't list them twice.
 */
export default function InHouseTiles({ searchQuery = "", onLoaded }) {
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState(null);
  const [error, setError] = useState("");

  const [attempt, setAttempt] = useState(0);
  // In-house integrations are providers, so their logos come from the same endpoint as the catalog's.
  const logos = useProviderLogos(integrations);

  useEffect(() => {
    let current = true;
    setError("");
    setIntegrations(null);
    listInHouse().then((result) => {
      if (!current) return;
      if (result.success) {
        setIntegrations(result.data);
        onLoaded?.(result.data.map((m) => m.id));
      } else {
        setIntegrations([]);
        setError(`Couldn't load the integrations you're building. ${result.message}`);
        onLoaded?.([]); // let the Dashboard show its own list instead of waiting
      }
    });
    return () => {
      current = false;
    };
    // Reload only on mount and on Retry; onLoaded is a fresh closure on every Dashboard render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const q = searchQuery.trim().toLowerCase();
  const visible = (integrations || []).filter(
    (m) => !q || m.name.toLowerCase().includes(q) || (m.description || "").toLowerCase().includes(q)
  );

  return (
    <>
      <button
        type="button"
        onClick={() => navigate("/integrations/in-house/new")}
        className="group flex h-full min-h-[300px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-white px-6 text-center transition hover:border-blue-500 hover:bg-blue-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 transition group-hover:bg-blue-600">
          <Plus className="h-7 w-7 text-blue-600 transition group-hover:text-white" />
        </span>
        <span className="text-lg font-semibold text-gray-900">Add your API</span>
        <span className="mt-1 max-w-[240px] text-sm text-gray-500">
          Connect an internal system or vendor API. Paste a docs link or set it up by hand.
        </span>
      </button>

      {error && (
        <div className="col-span-full flex flex-wrap items-center gap-3">
          <ErrorNote message={error} />
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className="mt-4 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50"
          >
            Retry
          </button>
        </div>
      )}
      {integrations === null &&
        [0, 1].map((i) => (
          <div key={i} className="min-h-[300px] animate-pulse rounded-2xl border border-gray-200 bg-white px-6 pt-6">
            <div className="mb-2 h-24 w-24 rounded-xl bg-gray-200" />
            <div className="h-6 w-32 rounded bg-gray-200" />
            <div className="mt-4 h-10 rounded bg-gray-200" />
          </div>
        ))}

      {integrations !== null && q && visible.length === 0 && integrations.length > 0 && (
        <p className="col-span-full text-sm text-gray-500">No in-house integrations match “{searchQuery.trim()}”.</p>
      )}

      {visible.map((m) => {
        const status = statusOf(m);
        const live = m.endpoints.filter((e) => e.live).length;
        return (
          // A <button> centres its content vertically; flex-col keeps it top-aligned like the
          // other cards when the grid row is taller than this card.
          <button
            type="button"
            key={m.id}
            onClick={() => navigate(`/integrations/in-house/${m.id}`)}
            className="flex h-full min-w-0 flex-col rounded-2xl border border-gray-200 bg-white px-6 pb-8 pt-6 text-left transition-shadow hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <div className="mb-4 flex w-full items-start justify-between gap-3">
              <div className="flex min-w-0 flex-col items-start">
                <IntegrationLogo name={m.name} logoUrl={logos[m.id]} className="mb-2 h-24 w-24 text-3xl" />
                <h3 title={m.name} className="line-clamp-2 break-words text-xl font-bold text-gray-900 [overflow-wrap:anywhere]">
                  {m.name}
                </h3>
              </div>
              <span className="shrink-0">
                <StatusBadge status={status} />
              </span>
            </div>
            <p className="mb-6 line-clamp-2 min-h-[40px] w-full break-words text-sm text-gray-600">
              {m.description || "No description yet."}
            </p>
            <div className="mb-6 w-full border-b border-gray-300" />
            <div className="w-full space-y-1.5 pb-6">
              {m.endpoints.length === 0 && <p className="text-sm text-gray-500">No calls yet.</p>}
              {m.endpoints.slice(0, 3).map((e, i) => (
                <div key={i} className="flex min-w-0 items-center gap-2 text-sm">
                  <span className="w-14 shrink-0 font-mono text-[11px] font-semibold text-gray-500">{e.method}</span>
                  <span className="truncate text-gray-800">{e.name}</span>
                </div>
              ))}
              {m.endpoints.length > 3 && <p className="text-xs text-gray-500">+{m.endpoints.length - 3} more</p>}
            </div>
            <div className="mt-auto flex w-full items-center justify-between gap-2">
              <span className="truncate rounded-lg bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                {status === "Draft" ? "In-House" : `In-House · ${live} of ${m.endpoints.length} live`}
              </span>
              <span className="shrink-0 text-sm font-medium text-blue-600">{status === "Draft" ? "Continue setup" : "Manage"}</span>
            </div>
          </button>
        );
      })}
    </>
  );
}
